<template>
  <section class="fg-results">
    <header class="fg-page__header">
      <BackButton @click="router.goTo(BookingStep.Search)" />
      <div class="fg-page__titles">
        <h1 class="fg-page__title">{{ t('results.title') }}</h1>
        <p class="fg-page__subtitle">{{ t('results.subtitle') }}</p>
      </div>
      <Stepper current="trip" />
    </header>

    <!-- Rappel de la recherche (trajet/dates/passagers/véhicule) + accès rapide
         à "Modifier" — présent dans tous les états (chargement/erreur/résultats)
         pour que l'user puisse toujours vérifier/corriger ses critères. -->
    <SearchRecap />

    <!-- Skeleton pendant l'appel availability (navigation optimiste : on arrive
         ici AVANT la réponse, le skeleton tient la page). -->
    <SkeletonRow v-if="store.loading" :label="t('results.searching')" />

    <!-- Échec de l'appel availability : état d'erreur dédié (on reste sur
         Résultats). L'ErrorBanner global est masqué ici (cf. Shell.vue). -->
    <div v-else-if="store.error" class="fg-results-error">
      <div class="fg-results-error__icon"><Icon name="ship" :size="26" /></div>
      <h2 class="fg-results-error__title">{{ t('results.errorTitle') }}</h2>
      <p class="fg-results-error__text">{{ t('results.errorText') }}</p>
      <div class="fg-results-error__actions">
        <button type="button" class="fg-btn fg-btn--primary" @click="retry">{{ t('results.retry') }}</button>
        <button type="button" class="fg-btn" @click="router.goTo(BookingStep.Search)">{{ t('results.modifySearch') }}</button>
      </div>
    </div>

    <!-- Vide si : aucun aller, OU recherche A/R sans aucun retour (sinon
         le CTA Continuer resterait désactivé sans issue). -->
    <div v-else-if="outwardSailings.length === 0 || (isReturn && returnSailings.length === 0)" class="fg-noresults">
      <div class="fg-noresults__icon"><Icon name="ship" :size="26" /></div>
      <h2 class="fg-noresults__title">{{ t('results.emptyTitle') }}</h2>
      <p class="fg-noresults__text">{{ t('results.emptyText') }}</p>
      <button type="button" class="fg-btn fg-btn--primary" @click="router.goTo(BookingStep.Search)">
        {{ t('results.newSearch') }}
      </button>
    </div>

    <div
      v-else
      class="fg-page-with-summary"
      :class="{ 'fg-page-with-summary--full': !store.selectedSailing.outward }"
    >
      <div class="fg-page-with-summary__main">
      <article class="fg-trip">
        <header class="fg-trip__head">
          <span class="fg-trip__head-icon"><Icon name="ship" :size="16" /></span>
          <span class="fg-trip__head-text">
            <span class="fg-trip__head-eyebrow">{{ t('results.outwardTrip') }}</span>
            <span v-if="outwardRoute" class="fg-trip__head-route">
              {{ outwardRoute.from }} <span class="fg-trip__head-arrow">→</span> {{ outwardRoute.to }}
            </span>
          </span>
        </header>

        <div class="fg-trip__steps">
          <section class="fg-step">
            <div class="fg-step__body">
              <p v-if="outwardSailings.length > 1" class="fg-results-count">
                {{ t('results.availableCount', { n: outwardSailings.length }) }}
              </p>
              <p v-if="!store.selectedSailing.outward" class="fg-step__hint">
                <Icon name="info" :size="13" />{{ t('results.pickSailingHint') }}
              </p>
              <!-- Le choix de place s'ouvre EN PLACE sous le départ sélectionné
                   (pas en bas de liste, invisible quand la liste est longue). -->
              <template v-for="sailing in outwardSailings" :key="`out-${sailing.sailingId}`">
                <SailingRow
                  :sailing="sailing"
                  :selected="store.selectedSailing.outward === sailing.sailingId"
                  :locale="locale"
                  :is-fastest="sailing.sailingId === outwardFastestId"
                  :is-cheapest="sailing.sailingId === outwardCheapestId"
                  @select="pickSailing('outward', sailing)"
                />
                <div
                  v-if="store.selectedSailing.outward === sailing.sailingId && sailing.hasPlaces"
                  ref="outwardInlineRef"
                  class="fg-step__inline"
                >
                  <h3 class="fg-step__inline-title">{{ needsGroup2 ? groupPlaceTitle(1) : t('results.choosePlaceRequired') }}</h3>
                  <PlaceList direction="outward" :group="1" />
                  <template v-if="needsGroup2">
                    <h3 class="fg-step__inline-title">{{ groupPlaceTitle(2) }}</h3>
                    <PlaceList direction="outward" :group="2" />
                  </template>
                </div>
              </template>
            </div>
          </section>
        </div>
      </article>

      <article v-if="returnSailings.length > 0" class="fg-trip">
        <header class="fg-trip__head">
          <span class="fg-trip__head-icon"><Icon name="ship" :size="16" /></span>
          <span class="fg-trip__head-text">
            <span class="fg-trip__head-eyebrow">{{ t('results.returnTrip') }}</span>
            <span v-if="returnRoute" class="fg-trip__head-route">
              {{ returnRoute.from }} <span class="fg-trip__head-arrow">→</span> {{ returnRoute.to }}
            </span>
          </span>
        </header>

        <div class="fg-trip__steps">
          <section class="fg-step">
            <div class="fg-step__body">
              <p v-if="returnSailings.length > 1" class="fg-results-count">
                {{ t('results.availableCount', { n: returnSailings.length }) }}
              </p>
              <p v-if="!store.selectedSailing.return" class="fg-step__hint">
                <Icon name="info" :size="13" />{{ t('results.pickSailingHint') }}
              </p>
              <template v-for="sailing in returnSailings" :key="`ret-${sailing.sailingId}`">
                <SailingRow
                  :sailing="sailing"
                  :selected="store.selectedSailing.return === sailing.sailingId"
                  :locale="locale"
                  :is-fastest="sailing.sailingId === returnFastestId"
                  :is-cheapest="sailing.sailingId === returnCheapestId"
                  @select="pickSailing('return', sailing)"
                />
                <div
                  v-if="store.selectedSailing.return === sailing.sailingId && sailing.hasPlaces"
                  ref="returnInlineRef"
                  class="fg-step__inline"
                >
                  <h3 class="fg-step__inline-title">{{ needsGroup2 ? groupPlaceTitle(1) : t('results.choosePlaceRequired') }}</h3>
                  <PlaceList direction="return" :group="1" />
                  <template v-if="needsGroup2">
                    <h3 class="fg-step__inline-title">{{ groupPlaceTitle(2) }}</h3>
                    <PlaceList direction="return" :group="2" />
                  </template>
                </div>
              </template>
            </div>
          </section>
        </div>
      </article>

      <Transition name="fg-fade">
        <article v-if="tariffAvailabilities.length > 0" class="fg-trip fg-trip--tariff">
          <header class="fg-trip__head">
            <span class="fg-trip__head-icon fg-trip__head-icon--euro" aria-hidden="true">€</span>
            <span class="fg-trip__head-text">
              <span class="fg-trip__head-eyebrow">{{ t('results.stepTariff') }}</span>
              <span class="fg-trip__head-route fg-trip__head-route--sm">{{ t('tariff.subtitle') }}</span>
            </span>
          </header>

          <div class="fg-trip__steps">
            <TariffSection :availabilities="tariffAvailabilities" />
          </div>
        </article>
      </Transition>

      <!-- Toujours visible dès l'arrivée sur l'écran (pas seulement une fois un
           horaire choisi) : donne un repère permanent de "où mène le parcours",
           en état verrouillé (hint) tant que traversée + place ne sont pas
           choisies, puis bascule sur le total dès qu'un tarif est dérivé. Le
           bouton reste disabled tant que `canContinue` n'est pas vrai. -->
      <footer class="fg-actionbar">
        <div class="fg-actionbar__total">
          <template v-if="hasAnyTariff">
            <span class="fg-actionbar__total-label">{{ t('results.total') }}</span>
            <span class="fg-actionbar__total-value">{{ totalFormatted }}</span>
          </template>
          <span v-else class="fg-actionbar__hint">
            <Icon name="info" :size="13" />{{ t('results.selectToContinue') }}
          </span>
          <!-- Indépendant de hasAnyTariff : un groupe sans AUCUNE place se sait
               dès le choix du sailing, avant même que l'autre groupe ait un tarif
               dérivé — l'avertissement ne doit pas attendre inutilement. -->
          <span
            v-if="noAvailability"
            class="fg-actionbar__total-note fg-actionbar__total-note--warn"
          >{{ t('results.noAvailabilityDate') }}</span>
          <span v-else-if="hasAnyTariff" class="fg-actionbar__total-note">{{ t('summary.taxesIncluded') }}</span>
        </div>
        <button
          type="button"
          class="fg-btn fg-btn--primary"
          :disabled="!canContinue"
          @click="continueNext"
        >
          {{ t('results.continue') }}
        </button>
      </footer>
      </div><!-- /.fg-page-with-summary__main -->

      <!-- Masqué tant qu'aucune traversée n'est choisie : le rappel (SearchRecap)
           couvre déjà ce rôle avant sélection, inutile de gaspiller l'espace
           avec une carte "vide". -->
      <BookingSummary
        v-if="store.selectedSailing.outward"
        class="fg-page-with-summary__aside"
        :show-continue="!!store.selectedSailing.outward"
        :continue-disabled="!canContinue"
        @continue="continueNext"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch, watchEffect } from 'vue'
import { useStore } from '../store'
import { useI18n } from '../i18n'
import { useRouter } from '../router'
import { useTracker } from '../tracker'
import { routeKey } from '../utils/track-meta'
import { useSearch } from '../composables/useSearch'
import { activeGroups, availabilitiesForGroup, availabilityForPlace, canCombineLegs, cheapestAvailability, companyKey, computeTariffsTotal, groupKey, isRoundtripLocked, passengersForGroup, pickCheapestByType, pickCheapestTariff, requiresSecondGroup, sailingKey, tariffDisplayAmount, tariffDisplayCurrency } from '../../../shared/rules'
import type { GroupNumber } from '../../../shared/rules'
import { BookingStep, Direction } from '../../../shared/types/enums'
import type { Availability, Tariff } from '../../../shared/types'
import { formatPort, formatPrice } from '../utils/format'
import { hasMealOptions } from '../utils/meals'
import BackButton from './atoms/BackButton.vue'
import SkeletonRow from './atoms/SkeletonRow.vue'
import SailingRow from './atoms/SailingRow.vue'
import PlaceList from './atoms/PlaceList.vue'
import TariffSection from './atoms/TariffSection.vue'
import BookingSummary from './atoms/BookingSummary.vue'
import SearchRecap from './atoms/SearchRecap.vue'
import Stepper from './atoms/Stepper.vue'
import Icon from './Icon.vue'

export interface Sailing {
  sailingId: string
  primary: Availability
  all: Availability[]
  hasPlaces: boolean
}

const store = useStore()
const router = useRouter()
const { t, locale } = useI18n()
const tracker = useTracker()
const search = useSearch()

// « Réessayer » depuis l'état d'erreur : rejoue la recherche (skeleton → résultats).
function retry(): void {
  void search.run()
}

const isReturn = computed(() => store.search.direction === Direction.Return)

// Découpage en sous-groupes : > 4 passagers ⇒ le backend renvoie un group 2
// (reliquat) et l'user choisit une place pour CHAQUE groupe.
const passengerCount = computed(() => store.search.passengers.length)
const needsGroup2 = computed(() => requiresSecondGroup(passengerCount.value))

function groupPlaceTitle(group: GroupNumber): string {
  return t('results.placeGroup', { group, count: passengersForGroup(store.search.passengers, group).length })
}

const outwardSailings = computed(() => groupSailings(store.availabilities.outward.all ?? []))
const returnSailingsAll = computed(() => groupSailings(store.availabilities.return.all ?? []))

const selectedOutward = computed(() => outwardSailings.value.find((s) => s.sailingId === store.selectedSailing.outward))

// Tarif A/R réduit (ex. Balearia) → valable seulement en A/R même compagnie.
function sailingLocked(s: Sailing): boolean {
  return isRoundtripLocked(s.primary)
}

// Appariement A/R (règle partagée widget/SaaS `canCombineLegs`) : un leg
// verrouillé ne se combine qu'avec la même compagnie. On filtre les retours
// proposés selon l'aller choisi → évite de réserver un aller simple au tarif
// normal alors qu'on a affiché le tarif A/R réduit.
const returnSailings = computed(() => {
  const out = selectedOutward.value
  if (!out) return returnSailingsAll.value
  const oc = companyKey(out.primary)
  const ol = sailingLocked(out)
  return returnSailingsAll.value.filter(
    (r) => canCombineLegs(oc, ol, companyKey(r.primary), sailingLocked(r)),
  )
})

const selectedReturn = computed(() => returnSailings.value.find((s) => s.sailingId === store.selectedSailing.return))

// Si l'aller choisi rend le retour sélectionné non combinable, on le purge.
watch(() => store.selectedSailing.outward, () => {
  const ret = store.selectedSailing.return
  if (ret && !returnSailings.value.some((s) => s.sailingId === ret)) {
    store.selectedSailing.return = null
    store.selectedPlaces.return = { group1: null, group2: null }
  }
})

// Route (Sète → Nador) affichée dans l'en-tête de chaque carte trajet, pour
// donner une identité claire au bloc au-delà des seules lignes de sailing.
const outwardRoute = computed<{ from: string; to: string } | null>(() => routeFor(store.availabilities.outward.all ?? []))
const returnRoute = computed<{ from: string; to: string } | null>(() => routeFor(store.availabilities.return.all ?? []))

function routeFor(list: Availability[]): { from: string; to: string } | null {
  const first = list[0]
  if (!first) return null
  return { from: formatPort(first.depart_name), to: formatPort(first.arrive_name) }
}

/** Availability portant la place choisie pour (direction, groupe). Restreinte
 *  aux availabilities du groupe (group_id) → l'`id`/`price_token` réservé
 *  correspond toujours au bon sous-process. `null` tant qu'aucune place n'est
 *  choisie (aucune présélection). Exception : un sailing SANS place à choisir →
 *  l'availability du groupe est directe (rien à sélectionner). */
function placeAvFor(direction: 'outward' | 'return', group: GroupNumber): Availability | null {
  const sailing = direction === 'outward' ? selectedOutward.value : selectedReturn.value
  if (!sailing) return null
  const groupAvs = availabilitiesForGroup(sailing.all, group)
  const placeId = store.selectedPlaces[direction][groupKey(group)]?.id ?? null
  const byPlace = availabilityForPlace(groupAvs, placeId)
  if (byPlace) return byPlace
  if (!groupHasPlaces(sailing, group)) return groupAvs[0] ?? null
  return null
}

const outwardAvG1 = computed(() => placeAvFor('outward', 1))
const outwardAvG2 = computed(() => placeAvFor('outward', 2))
const returnAvG1 = computed(() => placeAvFor('return', 1))
const returnAvG2 = computed(() => placeAvFor('return', 2))

// Availabilities (toutes directions/groupes) dont une place est choisie → la
// section tarif agrège leur prix : le total reflète bien les 2 groupes.
const tariffAvailabilities = computed(() => {
  const items: Array<{ direction: 'outward' | 'return'; availability: Availability | null }> = []
  const entries = [
    ['outward', outwardAvG1.value], ['outward', outwardAvG2.value],
    ['return', returnAvG1.value], ['return', returnAvG2.value],
  ] as const
  for (const [direction, availability] of entries) {
    if (availability) items.push({ direction, availability })
  }
  return items
})

/** Le groupe a-t-il des places à choisir sur ce sailing ? */
function groupHasPlaces(sailing: Sailing, group: GroupNumber): boolean {
  return availabilitiesForGroup(sailing.all, group).some((av) => (av.places?.length ?? 0) > 0)
}

/** Toutes les places requises (chaque groupe actif avec places) sont choisies ? */
function groupPlacesReady(direction: 'outward' | 'return'): boolean {
  const sailing = direction === 'outward' ? selectedOutward.value : selectedReturn.value
  if (!sailing) return false
  for (const group of activeGroups(passengerCount.value)) {
    if (groupHasPlaces(sailing, group) && !store.selectedPlaces[direction][groupKey(group)]) return false
  }
  return true
}

/** Un groupe actif SANS aucune place sur le sailing choisi → trajet impossible
 *  pour ce groupe. */
function hasUnavailableGroup(direction: 'outward' | 'return'): boolean {
  const sailing = direction === 'outward' ? selectedOutward.value : selectedReturn.value
  if (!sailing) return false
  return activeGroups(passengerCount.value).some((group) => !groupHasPlaces(sailing, group))
}

// Au moins un sous-groupe n'a aucune dispo sur un trajet requis (aller, ou
// retour en A/R) → on bloque « Continuer » : un dossier ne peut exister que si
// TOUS ses groupes sont réservables (chaque groupe = un dossier).
const noAvailability = computed<boolean>(() => {
  if (store.selectedSailing.outward && hasUnavailableGroup('outward')) return true
  if (isReturn.value && store.selectedSailing.return && hasUnavailableGroup('return')) return true
  return false
})

const canContinue = computed(() => {
  if (!store.selectedSailing.outward) return false
  if (noAvailability.value) return false
  if (!groupPlacesReady('outward')) return false
  if (isReturn.value) {
    if (!store.selectedSailing.return) return false
    if (!groupPlacesReady('return')) return false
  }
  return true
})

// Tarifs sélectionnés (4 slots aller/retour × group1/2), source unique du total
// affiché ET du montant numérique remonté au tracking.
const selectedTariffList = computed<Tariff[]>(() => [
  store.selectedTariffs.outward.group1, store.selectedTariffs.outward.group2,
  store.selectedTariffs.return.group1, store.selectedTariffs.return.group2,
].filter((t): t is Tariff => t !== null && t !== undefined))

// Total aligné sur la logique SaaS (presale = display + balance, option =
// balance, ticket = display) via la règle pure partagée.
const totalAmount = computed(() => computeTariffsTotal(selectedTariffList.value))
const totalCurrency = computed(() => tariffDisplayCurrency(selectedTariffList.value[0]))
const totalFormatted = computed(() => formatPrice(totalAmount.value, totalCurrency.value, locale.value))

// Payload « prix vu » remonté à l'hôte (bloc d'assistance WhatsApp Bladia). AUCUNE
// PII : à ce stade l'user n'a rien saisi. `value` = total NUMÉRIQUE (pas formaté),
// `currency` normalisée en ISO majuscule (le backend renvoie "Eur"/"Mad"). Noms de
// ports lisibles pris sur le catalogue journeys, comme `option_created`.
function priceMeta(): Record<string, unknown> {
  const primaryJourney = store.search.journeys.find((j) => j.direction === Direction.Outward)
    ?? store.search.journeys[0]
  return {
    value: totalAmount.value,
    currency: (totalCurrency.value ?? '').toUpperCase(),
    route: routeKey(store.search.journeys),
    originName: primaryJourney?.origin?.port_name,
    destinationName: primaryJourney?.destination?.port_name,
    passengers: store.search.passengers.length,
    sailingId: store.selectedSailing.outward ?? undefined,
  }
}

// Jalon funnel « prix vu » : dès que le total final existe (bouton Continuer
// actif), on émet `price_shown`, puis on le RÉ-ÉMET à chaque recalcul (changement
// de cabine/place/tarif) avec la nouvelle valeur — l'hôte veut toujours la
// dernière. On garde la dernière valeur émise pour ne pas ré-émettre à
// l'identique ; retour à un panier incomplet réarme l'émission.
let lastPriceValue: number | null = null
watch([canContinue, totalAmount, totalCurrency], () => {
  if (!canContinue.value) { lastPriceValue = null; return }
  if (totalAmount.value === lastPriceValue) return
  lastPriceValue = totalAmount.value
  tracker.emit('price_shown', priceMeta())
}, { immediate: true })

// Le footer bascule du hint verrouillé vers le total dès qu'AU MOINS un tarif
// est dérivé (place choisie sur au moins un groupe) — évite d'afficher "0,00 €"
// entre le choix du sailing et celui de la place.
const hasAnyTariff = computed<boolean>(() =>
  Boolean(
    store.selectedTariffs.outward.group1 || store.selectedTariffs.outward.group2
    || store.selectedTariffs.return.group1 || store.selectedTariffs.return.group2,
  ),
)

// Sailing le plus rapide / le moins cher de chaque liste — sert à badger
// SailingRow. `null` tant qu'il n'y a pas au moins 2 options (comparer 1 seul
// horaire à lui-même n'a pas de sens, cf. compteur "X traversées disponibles").
function fastestSailingId(list: Sailing[]): string | null {
  if (list.length < 2) return null
  let best: Sailing | null = null
  for (const s of list) {
    if (!best || (s.primary.duration ?? Infinity) < (best.primary.duration ?? Infinity)) best = s
  }
  return best?.sailingId ?? null
}
function cheapestSailingId(list: Sailing[]): string | null {
  if (list.length < 2) return null
  let bestId: string | null = null
  let bestAmount = Number.POSITIVE_INFINITY
  for (const s of list) {
    const av = cheapestAvailability(s.all)
    const tariff = av ? pickCheapestTariff(av) : null
    if (!tariff) continue
    const amount = tariffDisplayAmount(tariff)
    if (amount < bestAmount) { bestAmount = amount; bestId = s.sailingId }
  }
  return bestId
}
const outwardFastestId = computed(() => fastestSailingId(outwardSailings.value))
const outwardCheapestId = computed(() => cheapestSailingId(outwardSailings.value))
const returnFastestId = computed(() => fastestSailingId(returnSailings.value))
const returnCheapestId = computed(() => cheapestSailingId(returnSailings.value))

// Cible du scroll-into-view après un choix de sailing manuel (cf. pickSailing).
// v-for + v-if sur le même élément → Vue collecte le ref en tableau (0 ou 1
// entrée selon qu'un sailing est sélectionné pour cette direction).
const outwardInlineRef = ref<HTMLElement[]>([])
const returnInlineRef = ref<HTMLElement[]>([])

// Auto-pick du SAILING uniquement s'il n'y en a qu'un (pas de prix
// présélectionné : l'user choisit ensuite ses places). Sinon choix explicite.
//
// `watch(..., { immediate: true })` plutôt que `onMounted` : sous navigation
// OPTIMISTE (cf. useSearch.run), Results.vue est monté AVANT que la recherche
// résolve — au moment du onMounted, `outwardSailings` est encore vide, donc
// l'auto-pick ne se déclenchait jamais. Le watch réagit, lui, à l'arrivée
// réelle des résultats. `scroll: false` : pas de scroll auto-provoqué au
// chargement de la page (seul un clic explicite doit faire défiler la vue).
watch(outwardSailings, (list) => {
  if (!store.selectedSailing.outward && list.length === 1) pickSailing('outward', list[0]!, { scroll: false })
}, { immediate: true })
watch(returnSailings, (list) => {
  if (isReturn.value && !store.selectedSailing.return && list.length === 1) pickSailing('return', list[0]!, { scroll: false })
}, { immediate: true })

// Au changement de sailing → RESET des places du sens (AUCUNE présélection :
// l'user pioche). Nécessaire car les `place.id` sont des modèles catalogue
// partagés entre départs : un id resté sélectionné pourrait matcher une
// availability d'un AUTRE sailing.
watch(() => store.selectedSailing.outward, () => { store.selectedPlaces.outward = { group1: null, group2: null } })
watch(() => store.selectedSailing.return, () => { store.selectedPlaces.return = { group1: null, group2: null } })

// Tarif sélectionné = fonction PURE de (place choisie du groupe, type de la
// direction). Un SEUL effet dérive les 4 slots → plus de watchers impératifs,
// plus de risque d'ordre/boucle (l'effet écrit selectedTariffs sans le lire).
// On ne dérive le tarif que depuis l'availability de la PLACE réservée — elle
// porte l'`id`/`price_token` signé revérifié à la création (signed quote).
watchEffect(() => {
  for (const direction of ['outward', 'return'] as const) {
    const type = store.selectedTariffType[direction]
    for (const group of [1, 2] as const) {
      const av = placeAvFor(direction, group)
      store.selectedTariffs[direction][groupKey(group)] = av
        ? pickCheapestByType(av, type) || pickFirstAvailableTariff(av)
        : null
    }
  }
})

function pickFirstAvailableTariff(av: Availability): Tariff | null {
  for (const type of ['ticket', 'presale', 'option']) {
    const t = pickCheapestByType(av, type)
    if (t) return t
  }
  return null
}

function pickSailing(direction: 'outward' | 'return', sailing: Sailing, opts?: { scroll?: boolean }): void {
  // Aucune présélection de place/prix : on pose juste le sailing ; le watch
  // reset les places, l'user choisit, puis le watchEffect dérive le tarif.
  store.selectedSailing[direction] = sailing.sailingId
  // Jalon funnel : l'user a choisi une traversée (entre results_viewed et options).
  tracker.emitOnce('sailing_selected', {
    direction,
    sailingId: sailing.sailingId,
    route: routeKey(store.search.journeys),
  }, `sailing_selected:${direction}`)

  // Amène le choix de place à l'écran (il s'ouvre juste sous la ligne cliquée,
  // pas forcément dans le viewport sur mobile). Désactivé pour l'auto-pick au
  // chargement (opts.scroll === false) : un scroll surprise dès l'arrivée sur
  // la page serait déroutant, seul un clic explicite doit déclencher le défilement.
  if (opts?.scroll === false) return
  void nextTick(() => {
    const target = (direction === 'outward' ? outwardInlineRef.value : returnInlineRef.value)[0]
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

function continueNext(): void {
  // Même payload que price_shown → l'hôte mesure « prix vu » vs « prix validé ».
  tracker.emit('continue_clicked', priceMeta())
  router.goTo(hasMealOptions(store) ? BookingStep.Options : BookingStep.Booking)
}

function groupSailings(list: Availability[]): Sailing[] {
  const grouped = new Map<string, Availability[]>()
  for (const av of list) {
    const key = sailingKey(av)
    const arr = grouped.get(key)
    if (arr) arr.push(av); else grouped.set(key, [av])
  }
  const sailings: Sailing[] = []
  for (const avs of grouped.values()) {
    const primary = avs[0]!
    sailings.push({
      sailingId: sailingKey(primary),
      primary,
      all: avs,
      hasPlaces: avs.some((av) => (av.places?.length ?? 0) > 0),
    })
  }
  return sailings.sort((a, b) => (a.primary.depart_at ?? '').localeCompare(b.primary.depart_at ?? ''))
}
</script>
